/**
 * wav.ts — decode compressed audio bytes (mp3) and re-encode as a 16-bit
 * WAV blob, for OFFLINE playback only.
 *
 * Why: WebKit (desktop Safari AND iOS) refuses to play an <audio> element
 * whose src is a blob: URL backed by mp3 bytes from IndexedDB — it aborts
 * with "operation is not supported". But it plays WAV blobs fine; the old
 * driving-mode concatenation path proved this (it decoded everything to a
 * single 16-bit WAV blob). This is that same decode + encode, applied per
 * clip, so the existing <audio> player can play cached audio offline.
 *
 * Online streaming is untouched — it still hands the proxy /api/audio URL to
 * the audio element. This conversion runs only on the offline resolve path.
 *
 * Lifted from the deleted utils/audioConcatenator.ts (commit 59a17a7b^).
 */

import { levelClip } from './loudness'

const BITS_PER_SAMPLE = 16

let sharedCtx: AudioContext | null = null

function getCtx(): AudioContext | null {
  if (sharedCtx) return sharedCtx
  const Ctx = (typeof window !== 'undefined')
    ? ((window as any).AudioContext || (window as any).webkitAudioContext)
    : null
  if (!Ctx) return null
  try {
    sharedCtx = new Ctx() as AudioContext
  } catch {
    return null
  }
  return sharedCtx
}

function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i))
  }
}

/** Plain PCM — what the encoder needs, minus the Web Audio object. */
export interface PcmClip {
  sampleRate: number
  channels: Float32Array[]
}

/** Encode PCM channels to a 16-bit PCM WAV blob (audio/wav). */
export function encodePcmWav(clip: PcmClip): Blob {
  const numChannels = clip.channels.length
  const sampleRate = clip.sampleRate
  const bytesPerSample = BITS_PER_SAMPLE / 8
  const blockAlign = numChannels * bytesPerSample
  const byteRate = sampleRate * blockAlign
  const numSamples = clip.channels[0]?.length ?? 0
  const dataSize = numSamples * blockAlign
  const headerSize = 44

  const arrayBuffer = new ArrayBuffer(headerSize + dataSize)
  const view = new DataView(arrayBuffer)

  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeString(view, 8, 'WAVE')
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true)              // PCM fmt chunk size
  view.setUint16(20, 1, true)               // AudioFormat = PCM
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, BITS_PER_SAMPLE, true)
  writeString(view, 36, 'data')
  view.setUint32(40, dataSize, true)

  let byteOffset = headerSize
  for (let i = 0; i < numSamples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, clip.channels[ch][i]))
      view.setInt16(byteOffset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
      byteOffset += 2
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' })
}

function audioBufferToPcm(buffer: AudioBuffer): PcmClip {
  const channels: Float32Array[] = []
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) channels.push(buffer.getChannelData(ch))
  return { sampleRate: buffer.sampleRate, channels }
}

/**
 * Decode compressed bytes to plain PCM, for callers that splice clips
 * together (the intro assembler). Null when there is no AudioContext or the
 * decode fails — the caller falls back to the clip it would have played.
 */
export async function bytesToPcm(data: ArrayBuffer): Promise<PcmClip | null> {
  const ctx = getCtx()
  if (!ctx) return null
  try {
    return audioBufferToPcm(await ctx.decodeAudioData(data.slice(0)))
  } catch (err) {
    console.warn('[wav] decode failed:', err)
    return null
  }
}

/**
 * Decode mp3 (or any decodeAudioData-supported) bytes → 16-bit WAV blob,
 * LEVELLED to the one play-time loudness (cache/loudness.ts, ask #139). Every
 * clip the player plays from the device passes through here, so this is the
 * single place uneven recordings are evened out. Levelling never fails a
 * clip — on any error it is encoded as recorded.
 * Returns null if there's no AudioContext or decode fails (caller falls
 * back to the network URL).
 */
export async function bytesToWavBlob(data: ArrayBuffer): Promise<Blob | null> {
  const ctx = getCtx()
  if (!ctx) return null
  try {
    // decodeAudioData may detach the buffer — hand it a copy.
    const buffer = await ctx.decodeAudioData(data.slice(0))
    return encodePcmWav(levelClip(audioBufferToPcm(buffer)))
  } catch (err) {
    console.warn('[wav] decode failed:', err)
    return null
  }
}
